import { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Col, Modal, Row, Segmented } from 'antd';
import { TbArrowRight, TbSquareArrowRight, TbUsersPlus, TbX } from 'react-icons/tb';

import { ResourceDraft, useDirtyResourceDraft } from '@/hooks/useResourceDrafts';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { LearnMoreFooter } from '@/shared';
import { useTeamStore } from '@/stores';
import { SetStateMethod, TeamVisibilityType } from '@/types';
import { capitalizeFirstLetter } from '@/utilities';

import CreateMoreControl from '../components/CreateMoreControl';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';
import Create from './views/Create';
import Join from './views/Join';

interface CreateTeamModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    initialDraft?: ResourceDraft<any> | null;
    onDraftResolved?: () => void;
}

const CreateTeamModal: FC<CreateTeamModalProps> = ({ open, setOpen, initialDraft, onDraftResolved }): JSX.Element => {
    const [segmentedValue, setSegmentedValue] = useState('create');
    const [createMore, setCreateMore] = useState(false);
    const [visibility, setVisibility] = useState<TeamVisibilityType>(
        initialDraft?.values?.visibility ?? TeamVisibilityType.PRIVATE,
    );
    const [createFooterState, setCreateFooterState] = useState<{
        disabled: boolean;
        loading: boolean;
        onCreate?: () => void;
    }>({ disabled: true, loading: false });
    const [draftValues, setDraftValues] = useState({
        backgroundColor: initialDraft?.values?.backgroundColor ?? '',
        description: initialDraft?.values?.description ?? '',
        emoji: initialDraft?.values?.emoji ?? '',
        handle: initialDraft?.values?.handle ?? '',
        memberIds: initialDraft?.values?.memberIds ?? [],
        name: initialDraft?.values?.name ?? '',
        visibility,
    });
    const { teams } = useTeamStore();
    const createAccess = useWorkspaceResourceCreateAccess('teams', teams?.length ?? 0);

    useEffect(() => {
        if (open && initialDraft?.values) {
            setVisibility(initialDraft.values.visibility ?? TeamVisibilityType.PRIVATE);
        }
    }, [initialDraft, open]);

    const { dirtyDraftModal, markSubmitted, requestClose } = useDirtyResourceDraft({
        close: () => setOpen(false),
        draftId: initialDraft?.id,
        isDirty:
            draftValues.name.trim() !== '' ||
            draftValues.description.trim() !== '' ||
            draftValues.emoji !== '' ||
            draftValues.handle.trim() !== '' ||
            draftValues.memberIds.length > 0 ||
            draftValues.visibility !== 'Private',
        resourceLabel: 'Team',
        resourceType: 'team',
        title: draftValues.name,
        values: draftValues,
    });

    const viewMap = {
        create: (
            <Create
                open={open}
                setOpen={setOpen}
                initialDraft={initialDraft}
                onCreated={() => {
                    markSubmitted();
                    onDraftResolved?.();
                }}
                onDraftValuesChange={setDraftValues}
                createMore={createMore}
                disabled={createAccess.disabled}
                onFooterStateChange={setCreateFooterState}
                visibility={visibility}
                setVisibility={setVisibility}
            />
        ),
        join: <Join />,
    } as Record<string, ReactNode>;

    return (
        <>
            <Modal
                mask={false}
                open={open}
                onCancel={requestClose}
                onOk={requestClose}
                title={
                    segmentedValue === 'create' ? (
                        <ResourceCreationModalTitle
                            icon={<TbUsersPlus size='1rem' style={{ verticalAlign: '-0.175rem' }} />}
                            title='Create Team'
                            visibility={visibility}
                            setVisibility={(nextVisibility) => setVisibility(nextVisibility as TeamVisibilityType)}
                        />
                    ) : (
                        `${capitalizeFirstLetter(segmentedValue)} a Team`
                    )
                }
                style={{ minWidth: '37.5vw' }}
                bodyStyle={{ minHeight: '12rem' }}
                closable
                closeIcon={<TbX size='1rem' style={{ verticalAlign: '-0.2rem' }} onClick={requestClose} />}
                footer={
                    <Row align='middle'>
                        <Col flex='none'>
                            <LearnMoreFooter align='left' topic='Teams' />
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none' style={{ minHeight: '2rem' }}>
                            {segmentedValue === 'create' && (
                                <Row align='middle' gutter={[8, 0]} wrap={false}>
                                    <Col flex='none'>
                                        <CreateMoreControl
                                            checked={createMore}
                                            disabled={createAccess.disabled}
                                            onChange={setCreateMore}
                                        />
                                    </Col>
                                    <Col flex='none'>
                                        <Button onClick={requestClose}>Cancel</Button>
                                    </Col>
                                    <Col flex='none'>
                                        <Button
                                            type='primary'
                                            disabled={createFooterState.disabled || createAccess.disabled}
                                            loading={createFooterState.loading}
                                            onClick={createFooterState.onCreate}
                                        >
                                            Create Team
                                            <TbArrowRight
                                                style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }}
                                            />
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Row>
                }
            >
                <Segmented
                    options={[
                        {
                            label: (
                                <>
                                    <TbUsersPlus
                                        size='1rem'
                                        style={{ verticalAlign: '-0.2rem', marginRight: '0.25rem' }}
                                    />
                                    Create
                                </>
                            ),
                            value: 'create',
                        },
                        {
                            label: (
                                <>
                                    <TbSquareArrowRight
                                        size='1rem'
                                        style={{ verticalAlign: '-0.2rem', marginRight: '0.25rem' }}
                                    />
                                    Join
                                </>
                            ),
                            value: 'join',
                        },
                    ]}
                    block
                    style={{ marginBottom: '0.5rem', marginTop: '1rem' }}
                    value={segmentedValue}
                    onChange={(e: any) => setSegmentedValue(e)}
                />
                <div style={{ height: '20.25rem', overflowY: segmentedValue === 'create' ? 'auto' : 'hidden' }}>
                    {viewMap[segmentedValue]}
                </div>
            </Modal>
            {dirtyDraftModal}
        </>
    );
};

export default CreateTeamModal;
